// Dev Combat — pick any champions for both teams and fight instantly.
// Accessible at ?debug=true. Shift-click level buttons to step by 5.

import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { CHAMPIONS, TIER_COLORS, type ChampionId } from "../data/champions";
import { championIconUrl } from "../config/constants";
import { buildGhostUnit } from "../combat/UnitFactory";
import { RunState } from "../game/RunState";
import { XP_FOR_LEVEL } from "../units/UnitStats";
import { CombatScene } from "./CombatScene";
import { TrainingScene } from "./TrainingScene";
import { MainMenuScene } from "./MainMenuScene";
import type { MapNode } from "../map/CampaignMap";
import type { StarLevel } from "../game/RunState";

const MAX_TEAM = 5;
const FAKE_NODE: MapNode = { id: "dev", type: "boss", layer: 6, indexInLayer: 0, x: 0.5, y: 0.5 };
const ALL_CHAMPS = Object.keys(CHAMPIONS) as ChampionId[];

export class DevScene implements Scene {
  private root!: HTMLDivElement;

  private playerTeam: ChampionId[] = [];
  private enemyTeam: ChampionId[] = [];
  private playerLevel = 6;
  private enemyLevel = 6;
  private playerStars: StarLevel = 1;
  private enemyStars: StarLevel = 1;
  private playerRedBuff = false;
  private playerBlueBuff = false;
  private enemyRedBuff = false;
  private enemyBlueBuff = false;

  constructor(private game: Game) {}

  enter(): void {
    this.root = document.createElement("div");
    this.root.className = "dev-scene";
    this.root.innerHTML = this.buildHTML();
    this.game.sceneRoot.appendChild(this.root);
    this.attachListeners();
    this.refreshAll();
  }

  exit(): void {
    this.root.remove();
  }

  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}

  // ---- HTML ----

  private buildHTML(): string {
    return `
      <div class="dev-header">
        <span class="dev-title">⚔ DEV COMBAT</span>
        <div class="dev-header-nav">
          <button class="btn btn-sm ghost" id="dev-training">Training Room</button>
          <button class="btn btn-sm ghost" id="dev-menu">Main Menu</button>
        </div>
      </div>

      <div class="dev-body">
        ${this.teamPanel("player")}
        <div class="dev-vs">VS</div>
        ${this.teamPanel("enemy")}
      </div>

      <div class="dev-footer">
        <button class="btn btn-sm ghost" id="dev-clear">CLEAR ALL</button>
        <button class="btn" id="dev-fight" disabled>FIGHT!</button>
      </div>`;
  }

  private teamPanel(side: "player" | "enemy"): string {
    const label = side === "player" ? "YOUR TEAM" : "ENEMY TEAM";
    const cards = ALL_CHAMPS.map((id) => {
      const def = CHAMPIONS[id];
      const color = TIER_COLORS[def.tier];
      return `
        <div class="dev-champ-card" data-champ="${id}" data-side="${side}" title="${def.displayName}">
          <div class="dev-champ-icon" style="background-image:url('${championIconUrl(def.iconId)}');border-color:${color}"></div>
          <div class="dev-champ-name" style="color:${color}">${def.displayName}</div>
        </div>`;
    }).join("");

    return `
      <div class="dev-panel dev-panel-${side}">
        <div class="dev-panel-head">
          <span class="dev-team-label">${label}</span>
          <div class="dev-controls">
            <span class="dev-dim">Lv</span>
            <button class="dev-lvl-btn" data-side="${side}" data-delta="-1">−</button>
            <span class="dev-lvl-val" id="${side}-lvl">6</span>
            <button class="dev-lvl-btn" data-side="${side}" data-delta="1">+</button>
            <span class="dev-dim" style="margin-left:8px">★</span>
            ${(([1, 2, 3] as StarLevel[]).concat(this.game.debug ? [4 as StarLevel] : [])).map(s =>
              `<button class="dev-star-btn${s === 1 ? " active" : ""}" data-side="${side}" data-star="${s}">${"★".repeat(s)}</button>`
            ).join("")}
            <button class="dev-buff-btn dev-buff-red" data-side="${side}" data-buff="red" title="Red Buff: +50% AD, 15% omnivamp">R</button>
            <button class="dev-buff-btn dev-buff-blue" data-side="${side}" data-buff="blue" title="Blue Buff: every ability is cast twice">B</button>
          </div>
        </div>

        <div class="dev-roster" id="${side}-roster">
          <span class="dev-roster-empty">No champions — pick below</span>
        </div>

        <div class="dev-champ-grid" id="${side}-grid">${cards}</div>
      </div>`;
  }

  // ---- Listeners ----

  private attachListeners(): void {
    this.root.querySelector("#dev-menu")!.addEventListener("click", () =>
      this.game.sceneManager.transition(new MainMenuScene(this.game)));

    this.root.querySelector("#dev-training")!.addEventListener("click", () =>
      this.game.sceneManager.transition(new TrainingScene(this.game)));

    this.root.querySelector("#dev-clear")!.addEventListener("click", () => {
      this.playerTeam = [];
      this.enemyTeam = [];
      this.refreshAll();
    });

    this.root.querySelector("#dev-fight")!.addEventListener("click", () => this.startFight());

    // Level buttons (shift-click = ±5)
    this.root.querySelectorAll<HTMLElement>(".dev-lvl-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const side = btn.dataset.side as "player" | "enemy";
        const step = (e as MouseEvent).shiftKey ? 5 : 1;
        const delta = parseInt(btn.dataset.delta!) * step;
        if (side === "player") {
          this.playerLevel = Math.max(1, Math.min(18, this.playerLevel + delta));
          this.root.querySelector("#player-lvl")!.textContent = String(this.playerLevel);
        } else {
          this.enemyLevel = Math.max(1, Math.min(18, this.enemyLevel + delta));
          this.root.querySelector("#enemy-lvl")!.textContent = String(this.enemyLevel);
        }
      });
    });

    // Star buttons
    this.root.querySelectorAll<HTMLElement>(".dev-star-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const side = btn.dataset.side as "player" | "enemy";
        const star = parseInt(btn.dataset.star!) as StarLevel;
        if (side === "player") this.playerStars = star;
        else this.enemyStars = star;
        this.root.querySelectorAll<HTMLElement>(`.dev-star-btn[data-side="${side}"]`).forEach((b) =>
          b.classList.toggle("active", parseInt(b.dataset.star!) === star));
      });
    });

    // Buff toggle buttons (apply to entire side)
    this.root.querySelectorAll<HTMLElement>(".dev-buff-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const side = btn.dataset.side as "player" | "enemy";
        const buff = btn.dataset.buff as "red" | "blue";
        if (side === "player") {
          if (buff === "red") this.playerRedBuff = !this.playerRedBuff;
          else this.playerBlueBuff = !this.playerBlueBuff;
        } else {
          if (buff === "red") this.enemyRedBuff = !this.enemyRedBuff;
          else this.enemyBlueBuff = !this.enemyBlueBuff;
        }
        const isOn = side === "player"
          ? (buff === "red" ? this.playerRedBuff : this.playerBlueBuff)
          : (buff === "red" ? this.enemyRedBuff : this.enemyBlueBuff);
        btn.classList.toggle("active", isOn);
      });
    });

    // Champion grid clicks
    this.root.querySelectorAll<HTMLElement>(".dev-champ-card").forEach((card) => {
      card.addEventListener("click", () => {
        const side = card.dataset.side as "player" | "enemy";
        const id = card.dataset.champ as ChampionId;
        const team = side === "player" ? this.playerTeam : this.enemyTeam;
        if (team.length < MAX_TEAM) {
          team.push(id);
          this.refreshAll();
        }
      });
    });
  }

  // ---- Refresh ----

  private refreshAll(): void {
    this.refreshRoster("player");
    this.refreshRoster("enemy");
    this.refreshGridAvailability("player");
    this.refreshGridAvailability("enemy");
    const btn = this.root.querySelector<HTMLButtonElement>("#dev-fight")!;
    btn.disabled = this.playerTeam.length === 0 || this.enemyTeam.length === 0;
  }

  private refreshRoster(side: "player" | "enemy"): void {
    const team = side === "player" ? this.playerTeam : this.enemyTeam;
    const el = this.root.querySelector(`#${side}-roster`)!;

    if (team.length === 0) {
      el.innerHTML = `<span class="dev-roster-empty">No champions — pick below</span>`;
      return;
    }

    el.innerHTML = team.map((id, i) => {
      const def = CHAMPIONS[id];
      const color = TIER_COLORS[def.tier];
      return `
        <div class="dev-roster-chip">
          <div class="dev-chip-icon" style="background-image:url('${championIconUrl(def.iconId)}');border-color:${color}"></div>
          <span class="dev-chip-name" style="color:${color}">${def.displayName}</span>
          <button class="dev-chip-remove" data-side="${side}" data-idx="${i}" title="Remove">×</button>
        </div>`;
    }).join("");

    el.querySelectorAll<HTMLElement>(".dev-chip-remove").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const arr = btn.dataset.side === "player" ? this.playerTeam : this.enemyTeam;
        arr.splice(parseInt(btn.dataset.idx!), 1);
        this.refreshAll();
      });
    });
  }

  private refreshGridAvailability(side: "player" | "enemy"): void {
    const full = (side === "player" ? this.playerTeam : this.enemyTeam).length >= MAX_TEAM;
    this.root.querySelectorAll<HTMLElement>(`#${side}-grid .dev-champ-card`).forEach((card) =>
      card.classList.toggle("dev-full", full));
  }

  // ---- Fight ----

  private startFight(): void {
    if (this.playerTeam.length === 0 || this.enemyTeam.length === 0) return;

    const state = new RunState(Math.floor(Math.random() * 0x7fffffff));
    let uid = 0;

    for (let i = 0; i < this.playerTeam.length; i++) {
      state.roster.push({
        id: `dev_p${uid++}`,
        championId: this.playerTeam[i],
        xp: XP_FOR_LEVEL[this.playerLevel - 1] ?? 0,
        stars: this.playerStars,
        currentHp: null,
        pos: { q: i, r: 0 },
        bonuses: { hpMult: 1, adMult: 1 },
        itemId: null,
        redBuff: this.playerRedBuff,
        blueBuff: this.playerBlueBuff,
      });
    }

    const enemyUnits = this.enemyTeam.map((champId, i) => {
      const entry = {
        id: `dev_e${uid++}`,
        championId: champId,
        xp: XP_FOR_LEVEL[this.enemyLevel - 1] ?? 0,
        stars: this.enemyStars,
        currentHp: null,
        pos: { q: i, r: 0 },
        bonuses: { hpMult: 1, adMult: 1 },
        itemId: null,
      };
      const ghost = buildGhostUnit(entry, i, this.enemyLevel);
      ghost.redBuff = this.enemyRedBuff;
      ghost.blueBuff = this.enemyBlueBuff;
      return ghost;
    });

    state.pendingCombat = { node: FAKE_NODE, enemies: enemyUnits, fighterLimit: null };
    this.game.runState = state;

    this.game.sceneManager.transition(
      new CombatScene(this.game, FAKE_NODE, (_winner) => {
        this.game.runState = null;
        this.game.sceneManager.transition(this);
      }),
    );
  }
}
